import type { ComponentType, SVGProps } from 'react';
import {
  BellIcon,
  CloudArrowUpIcon,
  KeyIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

export type AdminNavigationKey = 'users' | 'access' | 'security' | 'uploads' | 'notifications' | 'maintenance';
export type AdminArea = 'workspace' | 'access_control' | 'security' | 'imports' | 'notifications';

export interface AdminNavigationItem {
  key: AdminNavigationKey;
  title: string;
  description: string;
  href: string;
  activePaths: readonly string[];
  requiredArea: AdminArea;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export const ADMIN_NAV_ITEMS: readonly AdminNavigationItem[] = [
  {
    key: 'users',
    title: 'Utenti e Workspace',
    description: 'Sincronizzazione, gruppi e utenti importati da Google Workspace.',
    href: '/admin/utenti',
    activePaths: [],
    requiredArea: 'workspace',
    icon: UserGroupIcon,
  },
  {
    key: 'access',
    title: 'Permessi e accessi',
    description: 'Configurazione granulare di hub, app e funzionalità per gruppi e utenti.',
    href: '/admin/access-control',
    activePaths: [],
    requiredArea: 'access_control',
    icon: KeyIcon,
  },
  {
    key: 'security',
    title: 'Sicurezza e Audit',
    description: 'Audit log, eventi di sicurezza e tracciabilità degli accessi.',
    href: '/admin/sicurezza',
    activePaths: [],
    requiredArea: 'security',
    icon: ShieldCheckIcon,
  },
  {
    key: 'uploads',
    title: 'Caricamenti e dati',
    description: 'Import di dataset, GISSS, fornitori e bridge dati.',
    href: '/admin/caricamenti',
    activePaths: ['admin/caricamento-dati', 'admin/data-imports', 'admin/caricamento-gisss', 'admin/caricamento-fornitori'],
    requiredArea: 'imports',
    icon: CloudArrowUpIcon,
  },
  {
    key: 'notifications',
    title: 'Notifiche e alert',
    description: 'Centro notifiche, destinatari e preferenze di comunicazione.',
    href: '/admin/notifiche',
    activePaths: [],
    requiredArea: 'notifications',
    icon: BellIcon,
  },
  {
    key: 'maintenance',
    title: 'Manutenzione e disponibilità',
    description: 'Gestisci indisponibilità globale e manutenzione dei singoli hub.',
    href: '/admin/manutenzione',
    activePaths: [],
    requiredArea: 'security',
    icon: WrenchScrewdriverIcon,
  },
];

export function getAdminNavigationItem(key: AdminNavigationKey): AdminNavigationItem {
  const item = ADMIN_NAV_ITEMS.find((candidate) => candidate.key === key);
  if (!item) throw new Error(`Admin navigation item not found: ${key}`);
  return item;
}

export function getVisibleAdminNavigationItems(
  canAccessArea: (requiredArea: AdminArea) => boolean,
): AdminNavigationItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => canAccessArea(item.requiredArea));
}

export function isAdminRoute(path: string): boolean {
  return path === '/admin' || path.startsWith('/admin/');
}
